import type {
	AssetTypeCode,
	ComplexityLevel,
	ReviewVerdict,
	ValidationAssetOutputKind,
	ValidationAssetStatus,
	WorkOrderKind,
	WorkOrderSource,
} from './work-order.enums.js'
import type { WorkOrderLinkSource } from './work-order-external.enums.js'
import type { IOriginalCode, IReferencedDocument, IWorkOrderExternalFields } from './work-order-external.interface.js'

/** A classification tuple: axis code → selected value codes. */
export type Selection = Record<string, string[]>

/**
 * A work order's persisted status: a `workflow_status.code` value defined by
 * whichever workflow the work order is assigned to, not a member of a closed
 * application enum. Structurally a string — membership in the assigned
 * workflow is verified at the service layer, not by this type.
 */
export type WorkflowStatusCode = string

/**
 * The syntactic shape every status code must have: a non-empty string of
 * lowercase ASCII letters, digits, and underscores, matching the shape
 * `workflow_status.code` rows already carry. The write boundary validates
 * against this pattern and never lowercases, trims, or otherwise rewrites
 * the submitted value.
 */
export const WORKFLOW_STATUS_CODE_PATTERN = /^[a-z0-9_]+$/

/**
 * The persisted work-order row — **workflow mechanics only**. Every descriptive
 * value (complexity, asset-type, brand, geography, campaign…) lives as
 * classification/`object_attribute` rows on the anchor, NOT here. `orderCode`
 * is the one descriptive value kept as a column because it needs a UNIQUE
 * constraint + fast lookup.
 */
export interface IWorkOrder extends IWorkOrderExternalFields {
	workOrderId: string // uuid v7 (internal; never displayed)
	tenantId: number
	orderCode: string // 'WO-NNNNNNN' — the human reference
	title: string
	briefing: string | null
	source: WorkOrderSource
	/** The order's id in the external system named by `source`; null for a manual order. */
	externalRef: string | null
	status: WorkflowStatusCode // a status code of the assigned workflow, not a closed enum member
	/** The project manager accountable for the order (a user-service uuid; no cross-service FK). */
	pmUserId: string | null
	/** False marks a petition handled outside the Content Factory; the overwhelming majority are inside, hence the `true` default. */
	isContentFactory: boolean
	dueAt: string | null // ISO datetime (date + time, SLA)
	parentWorkOrderId: string | null // optional single parent (sub-order/variant)
	/** Set on creation and never edited: the sync's role for a Workfront order, `asset_brief` for a manual one. */
	kind: WorkOrderKind
	statusChangedAt: Date // when status last changed (board default filter + card age)
	workflowId: string | null // the workflow this work order is assigned to; null until resolution is wired
	isActive: boolean
	createdBy: string | null
	createdAt: Date
	updatedAt: Date
	deletedAt: Date | null
}

/** A named related link attached to a work order (Veeva vault, Workfront, brief…). */
export interface IWorkOrderLink {
	linkId: string
	workOrderId: string
	name: string
	url: string
	source: WorkOrderLinkSource
	/** The external object the link mirrors (a Workfront document ID); null for a manual link. */
	externalRef: string | null
	createdAt: Date
}

/**
 * A work order hydrated with its classification-resolved descriptors + assignees
 * — the read shape returned by GET endpoints. The tuple/attributes are resolved
 * from the classification core, not stored on the row.
 */
export interface IWorkOrderWithContext extends IWorkOrder {
	tuple: Selection
	complexityLevel: ComplexityLevel | null
	assetTypeCode: AssetTypeCode | null
	/** The TARGET Veeva code (`veeva_code` axis). */
	veevaCode: string | null
	/** The original Veeva codes, in order. */
	originalCodes: IOriginalCode[]
	/** The Vault documents the brief references, in order. */
	referencedDocuments: IReferencedDocument[]
	durationSeconds: number | null
	/** The linked Campaign's immutable `campaignCode` — the same linkage files and messages use. */
	campaignCode: string | null
	assigneeIds: string[]
	links: IWorkOrderLink[]
}

/** A work order ↔ user assignment. */
export interface IWorkOrderAssignee {
	workOrderId: string
	userId: string
	assignedAt: Date
}

/**
 * One produced-file reference in a completed render's manifest. Each output
 * carries its own human `label` (e.g. "IG Mock", "PDF") decided by the template
 * at render time — the panel renders whatever label the output carries, so there
 * is no fixed kind→label map. `kind` is the short output slug; `fileId` is the
 * stored File row it points at.
 */
export interface ValidationAssetOutputRef {
	fileId: string
	kind: ValidationAssetOutputKind
	label: string
}

/**
 * The concrete manifest a completed render persists on the request row: the full
 * ordered set of produced outputs. Writers (worker port) and readers (frontend
 * panel) share this shape. Older rows may still carry the legacy flat
 * `{ fileIds: string[] }` — readers handle that defensively.
 */
export interface ValidationAssetResultManifest {
	outputs: ValidationAssetOutputRef[]
}

/**
 * The produced-file references a completed render writes back. Broad by design so
 * this contract doesn't couple to the worker's internals; the concrete shape the
 * worker writes and the panel reads is {@link ValidationAssetResultManifest}.
 */
export type ValidationAssetManifest = Record<string, unknown>

/**
 * A request to generate a validation asset (verification PDF + card mockups) for a
 * work order. Tracks the async render the pdf-generator-worker performs.
 * `version` increments per (workOrderId, assetType) so a regeneration never
 * overwrites prior outputs. `resultManifest` (the produced file refs) and `error` are
 * populated by the worker on completion; both null while `requested`/`rendering`.
 */
export interface IValidationAssetRequest {
	requestId: string // uuid v7
	tenantId: number
	workOrderId: string
	assetType: string // the piece being produced (e.g. AssetTypeCode)
	channel: string // target channel (e.g. instagram)
	status: ValidationAssetStatus
	version: number // per (workOrderId, assetType); starts at 1
	resultManifest: ValidationAssetManifest | null // produced file refs, written by the worker on success
	error: string | null // failure reason, written by the worker on failure
	requestedBy: string | null // acting user's uuid (null if unknown)
	isActive: boolean
	createdAt: Date
	updatedAt: Date
	deletedAt: Date | null
}

/**
 * One review mark on a deliverable (`deliverable_review` row). Rows are
 * append-only: a mark and an un-mark are both new rows, and the newest per
 * (order, file, stage) is the tile's current state. `statusId` is the review
 * stage's permanent id, so renaming the stage's code or label keeps its marks.
 */
export interface IDeliverableReview {
	reviewId: string // uuid v7
	tenantId: number
	workOrderId: string
	fileId: string
	statusId: string
	verdict: ReviewVerdict
	reviewedBy: string | null // acting user's uuid (null if unknown)
	reviewedAt: Date
}
